import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box, Button, Chip, CircularProgress, Alert, Typography, Paper,
  Grid, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, FormControlLabel,
  Switch, Stack, Stepper, Step, StepLabel, IconButton, Tooltip, Table,
  TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  ArrowBack, Add, PictureAsPdf, Edit, Delete, Search,
  CheckCircle, Cancel, Send, Calculate,
} from '@mui/icons-material';

import { projectsApi, quotesApi } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  Quote, QuoteStatus, QuoteCreate, QUOTE_STATUS_LABELS, QUOTE_STATUS_COLORS,
  AGE_BAND_OPTIONS, EPCLookupResult,
} from '../types/index.ts';

const GBP = (v?: number | null) => v != null ? `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—';

// ── Quote status chip ─────────────────────────────────────────────────────────
function StatusChip({ status }: { status: QuoteStatus }) {
  return <Chip label={QUOTE_STATUS_LABELS[status]} color={QUOTE_STATUS_COLORS[status]} size="small" />;
}

// ── Generate Quote dialog ─────────────────────────────────────────────────────
function GenerateQuoteDialog({
  open, onClose, projectId, postcode,
}: {
  open: boolean; onClose: () => void; projectId: number; postcode?: string;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [epcResult, setEpcResult] = useState<EPCLookupResult | null>(null);
  const [epcLoading, setEpcLoading] = useState(false);
  const [epcError, setEpcError] = useState('');
  const [formError, setFormError] = useState('');

  const [form, setForm] = useState<QuoteCreate>({
    project_id: projectId,
    use_epc: true,
    floor_area_m2: undefined,
    age_string: '',
    wall_insulation: '',
    loft_insulation: '',
    glazing_type: '',
    floor_insulated: false,
    labor_cost: 1500,
    bus_grant: 7500,
    notes: '',
    valid_days: 30,
  });

  const set = (k: keyof QuoteCreate, v: any) => setForm(f => ({ ...f, [k]: v }));

  const createMutation = useMutation(quotesApi.createQuote, {
    onSuccess: () => {
      queryClient.invalidateQueries(['quotes', projectId]);
      onClose();
      resetDialog();
    },
    onError: (e: any) => {
      const detail = e.response?.data?.detail;
      setFormError(Array.isArray(detail)
        ? detail.map((d: any) => d.msg).join('; ')
        : String(detail || 'Failed to create quote'));
    },
  });

  const resetDialog = () => {
    setStep(0); setEpcResult(null); setEpcError(''); setFormError('');
    setForm({ project_id: projectId, use_epc: true, floor_area_m2: undefined, age_string: '', wall_insulation: '', loft_insulation: '', glazing_type: '', floor_insulated: false, labor_cost: 1500, bus_grant: 7500, notes: '', valid_days: 30 });
  };

  const runEpcLookup = async () => {
    if (!postcode) return;
    setEpcLoading(true); setEpcError('');
    try {
      const result = await quotesApi.epcLookup(postcode);
      setEpcResult(result);
      if (result.found) {
        if (result.floor_area_m2) set('floor_area_m2', result.floor_area_m2);
        if (result.construction_age_band) {
          const bandToAge: Record<string, string> = {
            A: 'pre_1900', B: '1900_1929', C: '1930_1949', D: '1950_1966',
            E: '1967_1975', F: '1976_1982', G: '1983_1990', H: '1991_1995',
            I: '1996_2002', J: '2003_2006', K: '2007_2011', L: '2012_plus',
          };
          set('age_string', bandToAge[result.construction_age_band] || '');
        }
      }
    } catch {
      setEpcError('EPC lookup failed — fill in details manually below');
    } finally {
      setEpcLoading(false);
    }
  };

  const steps = ['Property data', 'Financial', 'Review & generate'];

  return (
    <Dialog open={open} onClose={() => { onClose(); resetDialog(); }} maxWidth="md" fullWidth>
      <DialogTitle>Generate Quote</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

        {/* ── Step 0: Property data ─────────────────────────────────────── */}
        {step === 0 && (
          <Stack spacing={2}>
            {postcode && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>EPC Lookup</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">Postcode: <b>{postcode}</b></Typography>
                  <Button
                    size="small" variant="outlined" startIcon={epcLoading ? <CircularProgress size={14} /> : <Search />}
                    onClick={runEpcLookup} disabled={epcLoading}
                  >
                    Fetch EPC data
                  </Button>
                </Stack>
                {epcError && <Alert severity="warning" sx={{ mt: 1 }}>{epcError}</Alert>}
                {epcResult?.found && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    EPC found · Rating: <b>{epcResult.epc_rating}</b> ·
                    Floor area: <b>{epcResult.floor_area_m2} m²</b> ·
                    Age band: <b>{epcResult.construction_age_band}</b>
                    {epcResult.walls_description && ` · ${epcResult.walls_description}`}
                  </Alert>
                )}
                {epcResult && !epcResult.found && (
                  <Alert severity="info" sx={{ mt: 1 }}>No EPC certificate found — fill in manually below</Alert>
                )}
              </Paper>
            )}

            <Stack direction="row" spacing={2}>
              <TextField
                label="Floor area (m²) *"
                type="number"
                value={form.floor_area_m2 ?? ''}
                onChange={e => set('floor_area_m2', e.target.value ? parseFloat(e.target.value) : undefined)}
                fullWidth size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Construction age</InputLabel>
                <Select label="Construction age" value={form.age_string || ''} onChange={e => set('age_string', e.target.value)}>
                  {AGE_BAND_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Wall insulation</InputLabel>
                <Select label="Wall insulation" value={form.wall_insulation || ''} onChange={e => set('wall_insulation', e.target.value)}>
                  <MenuItem value="">Unknown</MenuItem>
                  <MenuItem value="cavity_filled">Cavity — filled</MenuItem>
                  <MenuItem value="cavity_empty">Cavity — unfilled</MenuItem>
                  <MenuItem value="solid">Solid — uninsulated</MenuItem>
                  <MenuItem value="external">External insulation</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Loft insulation</InputLabel>
                <Select label="Loft insulation" value={form.loft_insulation || ''} onChange={e => set('loft_insulation', e.target.value)}>
                  <MenuItem value="">Unknown</MenuItem>
                  <MenuItem value="200mm_plus">200 mm or more</MenuItem>
                  <MenuItem value="100mm">100–150 mm</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Glazing</InputLabel>
                <Select label="Glazing" value={form.glazing_type || ''} onChange={e => set('glazing_type', e.target.value)}>
                  <MenuItem value="">Unknown</MenuItem>
                  <MenuItem value="triple">Triple glazed</MenuItem>
                  <MenuItem value="double">Double glazed</MenuItem>
                  <MenuItem value="single">Single glazed</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch checked={!!form.floor_insulated} onChange={e => set('floor_insulated', e.target.checked)} />}
                label="Floor insulated"
                sx={{ mt: 1 }}
              />
            </Stack>
          </Stack>
        )}

        {/* ── Step 1: Financial ─────────────────────────────────────────── */}
        {step === 1 && (
          <Stack spacing={2}>
            <Alert severity="info">
              Labor cost and BUS grant are pre-filled with typical values. Adjust as needed.
            </Alert>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Labour cost (£)"
                type="number"
                value={form.labor_cost}
                onChange={e => set('labor_cost', parseFloat(e.target.value) || 0)}
                fullWidth size="small"
                helperText="Installation, commissioning, first-year service"
              />
              <TextField
                label="BUS grant deduction (£)"
                type="number"
                value={form.bus_grant}
                onChange={e => set('bus_grant', parseFloat(e.target.value) || 0)}
                fullWidth size="small"
                helperText="Boiler Upgrade Scheme — £7,500 for ASHPs"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Quote valid for (days)"
                type="number"
                value={form.valid_days}
                onChange={e => set('valid_days', parseInt(e.target.value) || 30)}
                sx={{ width: 200 }} size="small"
              />
            </Stack>
            <TextField
              label="Notes (optional)"
              value={form.notes || ''}
              onChange={e => set('notes', e.target.value)}
              multiline rows={3} fullWidth size="small"
              placeholder="Any additional information to include in the quote…"
            />
          </Stack>
        )}

        {/* ── Step 2: Review ────────────────────────────────────────────── */}
        {step === 2 && (
          <Stack spacing={2}>
            <Alert severity="info">
              The system will calculate heat loss, match a heat pump from inventory, and auto-generate line items.
              You can edit the quote after creation.
            </Alert>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Property summary</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}><Typography variant="body2">Floor area: <b>{form.floor_area_m2 ?? '—'} m²</b></Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Age: <b>{AGE_BAND_OPTIONS.find(o => o.value === form.age_string)?.label || '—'}</b></Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Walls: <b>{form.wall_insulation || 'Unknown'}</b></Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Loft: <b>{form.loft_insulation || 'Unknown'}</b></Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Glazing: <b>{form.glazing_type || 'Unknown'}</b></Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">EPC data: <b>{epcResult?.found ? `Yes (${epcResult.epc_rating})` : 'Manual'}</b></Typography></Grid>
              </Grid>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Financial</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}><Typography variant="body2">Labour: <b>{GBP(form.labor_cost)}</b></Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">BUS grant: <b>− {GBP(form.bus_grant)}</b></Typography></Grid>
                <Grid item xs={6}><Typography variant="body2">Valid for: <b>{form.valid_days} days</b></Typography></Grid>
              </Grid>
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { onClose(); resetDialog(); }}>Cancel</Button>
        {step > 0 && <Button onClick={() => setStep(s => s - 1)}>Back</Button>}
        {step < 2 && (
          <Button
            variant="contained"
            onClick={() => {
              if (step === 0 && !form.floor_area_m2) { setFormError('Floor area is required'); return; }
              setFormError('');
              setStep(s => s + 1);
            }}
          >
            Next
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained" color="primary"
            onClick={() => createMutation.mutate(form)}
            disabled={createMutation.isLoading}
            startIcon={createMutation.isLoading ? <CircularProgress size={16} /> : <Add />}
          >
            Generate quote
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isManager, isSurveyor } = useAuth();
  const queryClient = useQueryClient();
  const projectId = parseInt(id || '0');

  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);

  const { data: project, isLoading: projectLoading, error: projectError } = useQuery(
    ['project', projectId],
    () => projectsApi.getProject(projectId),
    { enabled: !!projectId },
  );

  const { data: quotesData } = useQuery(
    ['quotes', projectId],
    () => quotesApi.getQuotes({ project_id: projectId }),
    { enabled: !!projectId },
  );

  const updateStatusMutation = useMutation(
    (args: { quoteId: number; status: QuoteStatus }) =>
      quotesApi.updateQuote(args.quoteId, { status: args.status }),
    { onSuccess: () => queryClient.invalidateQueries(['quotes', projectId]) },
  );

  const deleteMutation = useMutation(quotesApi.deleteQuote, {
    onSuccess: () => queryClient.invalidateQueries(['quotes', projectId]),
  });

  if (projectLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (projectError || !project) return <Alert severity="error">Project not found</Alert>;

  const quotes = quotesData?.quotes || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/projects')} variant="outlined">
          Projects
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>{project.title}</Typography>
          <Typography variant="body2" color="text.secondary">{project.project_number}</Typography>
        </Box>
        <Chip label={project.status_display} color="primary" />
        <Chip label={project.project_type_display} variant="outlined" />
      </Box>

      <Grid container spacing={3}>
        {/* ── Project info ───────────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Project details</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={1}>
              {project.description && <Typography variant="body2">{project.description}</Typography>}
              {project.system_size && (
                <Box><Typography variant="caption" color="text.secondary">System size</Typography>
                  <Typography variant="body2">{project.system_size}</Typography></Box>
              )}
              {project.estimated_cost && (
                <Box><Typography variant="caption" color="text.secondary">Estimated cost</Typography>
                  <Typography variant="body2">{GBP(project.estimated_cost)}</Typography></Box>
              )}
              {project.mcs_required && (
                <Chip label="MCS required" size="small" color="info" />
              )}
              {project.notes && (
                <Box><Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{project.notes}</Typography></Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* ── Quotes ─────────────────────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>Quotes</Typography>
              {(isManager || isSurveyor) && (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Calculate />}
                    onClick={() =>
                      navigate('/quotes/calculator', {
                        state: {
                          project: {
                            id: project?.id,
                            title: project?.title,
                            project_number: project?.project_number,
                          },
                        },
                      })
                    }
                  >
                    Calculator
                  </Button>
                  <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setQuoteDialogOpen(true)}>
                    Quick quote
                  </Button>
                </Stack>
              )}
            </Box>

            {quotes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography variant="body2">No quotes yet</Typography>
                {(isManager || isSurveyor) && (
                  <Button sx={{ mt: 1 }} onClick={() => setQuoteDialogOpen(true)} startIcon={<Add />}>
                    Generate first quote
                  </Button>
                )}
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quote #</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Heat loss</TableCell>
                    <TableCell align="right">Total (after grant)</TableCell>
                    <TableCell align="right">Valid until</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotes.map(q => (
                    <TableRow key={q.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/quotes/${q.id}`)}>
                      <TableCell><Typography variant="body2" fontWeight={600}>{q.quote_number}</Typography></TableCell>
                      <TableCell><StatusChip status={q.status} /></TableCell>
                      <TableCell align="right">{q.heat_loss_kw ? `${q.heat_loss_kw} kW` : '—'}</TableCell>
                      <TableCell align="right"><b>{GBP(q.total_after_grant)}</b></TableCell>
                      <TableCell align="right">{q.valid_until || '—'}</TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Stack direction="row" justifyContent="flex-end">
                          <Tooltip title="Download PDF">
                            <IconButton size="small" onClick={() => quotesApi.downloadPdf(q.id, q.quote_number)}>
                              <PictureAsPdf fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isManager && q.status === QuoteStatus.DRAFT && (
                            <Tooltip title="Mark as sent">
                              <IconButton size="small" onClick={() => updateStatusMutation.mutate({ quoteId: q.id, status: QuoteStatus.SENT })}>
                                <Send fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isManager && q.status === QuoteStatus.SENT && (
                            <>
                              <Tooltip title="Mark accepted">
                                <IconButton size="small" color="success" onClick={() => updateStatusMutation.mutate({ quoteId: q.id, status: QuoteStatus.ACCEPTED })}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Mark rejected">
                                <IconButton size="small" color="error" onClick={() => updateStatusMutation.mutate({ quoteId: q.id, status: QuoteStatus.REJECTED })}>
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {isManager && q.status !== QuoteStatus.ACCEPTED && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => deleteMutation.mutate(q.id)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Grid>
      </Grid>

      <GenerateQuoteDialog
        open={quoteDialogOpen}
        onClose={() => setQuoteDialogOpen(false)}
        projectId={projectId}
        postcode={undefined}
      />
    </Box>
  );
}
